import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Label } from '../components/ui/label';
import { Calendar, Printer, Download, ChevronLeft, ChevronRight, Loader2, Users, DollarSign, CalendarDays } from 'lucide-react';
import { reportsAPI } from '../services/api';
import { useToast } from '../hooks/use-toast';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';

const EventReports = () => {
  const [reportType, setReportType] = useState('weekly');
  const [loading, setLoading] = useState(false);
  const [weeklyData, setWeeklyData] = useState(null);
  const [monthlyData, setMonthlyData] = useState(null);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const reportRef = useRef(null);
  const { toast } = useToast();

  const fetchWeeklyReport = useCallback(async () => {
    try {
      setLoading(true);
      const dateStr = selectedDate.toISOString().split('T')[0];
      const data = await reportsAPI.getWeeklySummary(dateStr);
      setWeeklyData(data);
    } catch (error) {
      toast({ title: 'Error loading weekly report', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [selectedDate, toast]);

  const fetchMonthlyReport = useCallback(async () => {
    try {
      setLoading(true);
      const data = await reportsAPI.getMonthlySummary(selectedYear, selectedMonth);
      setMonthlyData(data);
    } catch (error) {
      toast({ title: 'Error loading monthly report', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [selectedYear, selectedMonth, toast]);

  useEffect(() => {
    if (reportType === 'weekly') {
      fetchWeeklyReport();
    } else {
      fetchMonthlyReport();
    }
  }, [reportType, selectedDate, selectedYear, selectedMonth, fetchWeeklyReport, fetchMonthlyReport]);

  const navigateWeek = (direction) => {
    const newDate = new Date(selectedDate);
    newDate.setDate(newDate.getDate() + (direction * 7));
    setSelectedDate(newDate);
  };

  const navigateMonth = (direction) => {
    let newMonth = selectedMonth + direction;
    let newYear = selectedYear;
    
    if (newMonth > 12) {
      newMonth = 1;
      newYear++;
    } else if (newMonth < 1) {
      newMonth = 12;
      newYear--;
    }
    
    setSelectedMonth(newMonth);
    setSelectedYear(newYear);
  };

  const handlePrint = () => {
    window.print();
  };

  const handleDownloadPDF = async () => {
    if (!reportRef.current) return;
    
    try {
      toast({ title: 'Generating PDF...' });
      const canvas = await html2canvas(reportRef.current, { scale: 2 });
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = (canvas.height * pdfWidth) / canvas.width;
      
      pdf.addImage(imgData, 'PNG', 0, 0, pdfWidth, pdfHeight);
      
      const filename = reportType === 'weekly' 
        ? `weekly-report-${weeklyData?.week_start}.pdf`
        : `monthly-report-${monthlyData?.month_name}-${monthlyData?.year}.pdf`;
      
      pdf.save(filename);
      toast({ title: 'PDF downloaded successfully!' });
    } catch (error) {
      toast({ title: 'Error generating PDF', variant: 'destructive' });
    }
  };

  const months = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  const getStatusColor = (status) => {
    switch (status) {
      case 'confirmed': return 'bg-green-100 text-green-800';
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'cancelled': return 'bg-red-100 text-red-800';
      case 'draft': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount || 0);
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 bg-[#faf9f6] min-h-screen" data-testid="event-reports-page">
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-[#1e3a5f] mb-1">Event Reports</h1>
          <p className="text-gray-600">View and print weekly or monthly event summaries</p>
        </div>
        <div className="flex gap-3">
          <Button onClick={handlePrint} variant="outline" className="border-[#1e3a5f] text-[#1e3a5f]" data-testid="print-report-btn">
            <Printer size={20} className="mr-2" />Print
          </Button>
          <Button onClick={handleDownloadPDF} className="bg-[#1e3a5f] hover:bg-[#2d5016] text-white" data-testid="download-pdf-btn">
            <Download size={20} className="mr-2" />Download PDF
          </Button>
        </div>
      </div>

      {/* Report Type Selection */}
      <Card className="border-none shadow-lg mb-6">
        <CardContent className="p-6">
          <div className="flex flex-wrap items-center gap-6">
            <div className="flex items-center gap-3">
              <Label className="text-[#1e3a5f] font-medium">Report Type:</Label>
              <Select value={reportType} onValueChange={setReportType}>
                <SelectTrigger className="w-40" data-testid="report-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="weekly">Weekly</SelectItem>
                  <SelectItem value="monthly">Monthly</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {reportType === 'weekly' ? (
              <div className="flex items-center gap-3">
                <Button variant="ghost" onClick={() => navigateWeek(-1)} data-testid="prev-week-btn">
                  <ChevronLeft size={20} />
                </Button>
                <span className="font-medium text-[#1e3a5f] min-w-[250px] text-center">
                  {weeklyData ? `${weeklyData.week_start_display} - ${weeklyData.week_end_display}` : 'Loading...'}
                </span>
                <Button variant="ghost" onClick={() => navigateWeek(1)} data-testid="next-week-btn">
                  <ChevronRight size={20} />
                </Button>
              </div>
            ) : (
              <div className="flex items-center gap-3">
                <Button variant="ghost" onClick={() => navigateMonth(-1)} data-testid="prev-month-btn">
                  <ChevronLeft size={20} />
                </Button>
                <div className="flex gap-2">
                  <Select value={selectedMonth.toString()} onValueChange={(v) => setSelectedMonth(parseInt(v))}>
                    <SelectTrigger className="w-32">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {months.map((month, idx) => (
                        <SelectItem key={month} value={(idx + 1).toString()}>{month}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Select value={selectedYear.toString()} onValueChange={(v) => setSelectedYear(parseInt(v))}>
                    <SelectTrigger className="w-24">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {[2024, 2025, 2026, 2027, 2028].map((year) => (
                        <SelectItem key={year} value={year.toString()}>{year}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <Button variant="ghost" onClick={() => navigateMonth(1)} data-testid="next-month-btn">
                  <ChevronRight size={20} />
                </Button>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {loading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="animate-spin text-[#1e3a5f]" size={48} />
        </div>
      ) : (
        <div ref={reportRef} className="print:m-0">
          {/* Summary Stats */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <Card className="border-none shadow-lg">
              <CardContent className="p-6 flex items-center gap-4">
                <div className="p-3 rounded-full bg-[#1e3a5f]/10">
                  <CalendarDays size={24} className="text-[#1e3a5f]" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">Total Events</p>
                  <p className="text-2xl font-bold text-[#1e3a5f]" data-testid="total-events">
                    {reportType === 'weekly' ? weeklyData?.total_events : monthlyData?.total_events}
                  </p>
                </div>
              </CardContent>
            </Card>
            <Card className="border-none shadow-lg">
              <CardContent className="p-6 flex items-center gap-4">
                <div className="p-3 rounded-full bg-green-100">
                  <Users size={24} className="text-green-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">Total Guests</p>
                  <p className="text-2xl font-bold text-green-600" data-testid="total-guests">
                    {reportType === 'weekly' ? weeklyData?.total_guests : monthlyData?.total_guests}
                  </p>
                </div>
              </CardContent>
            </Card>
            <Card className="border-none shadow-lg">
              <CardContent className="p-6 flex items-center gap-4">
                <div className="p-3 rounded-full bg-[#c9a227]/20">
                  <DollarSign size={24} className="text-[#c9a227]" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">Total Revenue</p>
                  <p className="text-2xl font-bold text-[#c9a227]" data-testid="total-revenue">
                    {formatCurrency(reportType === 'weekly' ? weeklyData?.total_revenue : monthlyData?.total_revenue)}
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Weekly Report */}
          {reportType === 'weekly' && weeklyData && (
            <Card className="border-none shadow-lg">
              <CardHeader className="bg-gradient-to-r from-[#1e3a5f] to-[#2d5016] text-white rounded-t-lg">
                <CardTitle className="flex items-center gap-2">
                  <Calendar size={20} />
                  Weekly Event Summary: {weeklyData.week_start_display} - {weeklyData.week_end_display}
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                {weeklyData.days?.map((day, idx) => (
                  <div key={idx} className={`p-4 ${idx % 2 === 0 ? 'bg-gray-50' : 'bg-white'}`}>
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-24 font-bold text-[#1e3a5f]">{day.day_name}</div>
                      <div className="text-gray-500">{new Date(day.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</div>
                      <div className="ml-auto text-sm text-gray-500">{day.events?.length || 0} event(s)</div>
                    </div>
                    {day.events?.length > 0 ? (
                      <div className="space-y-2 ml-6">
                        {day.events.map((event, eventIdx) => (
                          <div key={eventIdx} className="flex items-center gap-4 p-3 bg-white rounded-lg border">
                            <div className="flex-1">
                              <p className="font-medium text-[#1e3a5f]">
                                {event.event_name}
                                {event.is_standalone_beo && (
                                  <span className="ml-2 text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded">BEO Only</span>
                                )}
                              </p>
                              <p className="text-sm text-gray-500">
                                {event.client_name}
                                {event.secondary_client_name && ` & ${event.secondary_client_name}`}
                              </p>
                            </div>
                            <div className="text-center">
                              <p className="text-sm font-medium">{event.event_time}</p>
                              <p className="text-xs text-gray-500">{event.guest_count} guests</p>
                            </div>
                            <div className="text-center min-w-[100px]">
                              <p className="text-sm">{event.location?.substring(0, 25)}</p>
                            </div>
                            <div className={`px-2 py-1 rounded text-xs font-medium ${getStatusColor(event.status)}`}>
                              {event.status}
                            </div>
                            <div className="text-right min-w-[80px]">
                              <p className="font-medium text-[#c9a227]">{formatCurrency(event.total_amount)}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-gray-400 text-sm ml-6 italic">No events scheduled</p>
                    )}
                  </div>
                ))}
              </CardContent>
            </Card>
          )}

          {/* Monthly Report */}
          {reportType === 'monthly' && monthlyData && (
            <Card className="border-none shadow-lg">
              <CardHeader className="bg-gradient-to-r from-[#1e3a5f] to-[#2d5016] text-white rounded-t-lg">
                <CardTitle className="flex items-center gap-2">
                  <Calendar size={20} />
                  Monthly Event Summary: {monthlyData.month_display}
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                {monthlyData.weeks?.map((week, idx) => (
                  <div key={idx} className={`p-4 ${idx % 2 === 0 ? 'bg-gray-50' : 'bg-white'}`}>
                    <div className="flex items-center gap-3 mb-3 pb-2 border-b">
                      <div className="font-bold text-[#1e3a5f]">Week {week.week_number}</div>
                      <div className="text-gray-500">{week.week_start_display} - {week.week_end_display}</div>
                      <div className="ml-auto flex gap-4 text-sm">
                        <span>{week.event_count} events</span>
                        <span>{week.total_guests} guests</span>
                        <span className="text-[#c9a227] font-medium">{formatCurrency(week.total_revenue)}</span>
                      </div>
                    </div>
                    {week.events?.length > 0 ? (
                      <div className="space-y-2 ml-4">
                        {week.events.map((event) => (
                          <div key={event.id || event.contract_id || `${event.event_date}-${event.event_name}`} className="flex items-center gap-4 p-3 bg-white rounded-lg border">
                            <div className="w-20 text-center">
                              <p className="font-medium text-[#1e3a5f]">
                                {new Date(event.event_date).toLocaleDateString('en-US', { weekday: 'short' })}
                              </p>
                              <p className="text-xs text-gray-500">
                                {new Date(event.event_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                              </p>
                            </div>
                            <div className="flex-1">
                              <p className="font-medium text-[#1e3a5f]">
                                {event.event_name}
                                {event.is_standalone_beo && (
                                  <span className="ml-2 text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded">BEO Only</span>
                                )}
                              </p>
                              <p className="text-sm text-gray-500">
                                {event.client_name}
                                {event.secondary_client_name && ` & ${event.secondary_client_name}`}
                              </p>
                            </div>
                            <div className="text-center">
                              <p className="text-sm">{event.event_time}</p>
                              <p className="text-xs text-gray-500">{event.guest_count} guests</p>
                            </div>
                            <div className={`px-2 py-1 rounded text-xs font-medium ${getStatusColor(event.status)}`}>
                              {event.status}
                            </div>
                            <div className="text-right min-w-[80px]">
                              <p className="font-medium text-[#c9a227]">{formatCurrency(event.total_amount)}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-gray-400 text-sm ml-4 italic">No events this week</p>
                    )}
                  </div>
                ))}
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* Print Styles */}
      <style>{`
        @media print {
          body * { visibility: hidden; }
          #root { visibility: visible; }
          [data-testid="event-reports-page"] { 
            visibility: visible; 
            position: absolute;
            left: 0;
            top: 0;
            width: 100%;
          }
          [data-testid="event-reports-page"] * { visibility: visible; }
          button, .no-print { display: none !important; }
        }
      `}</style>
    </div>
  );
};

export default EventReports;
